'use client'

import {Button} from '@/components/ui/button'
import {CloseIcon} from '@/components/ui/icons/CloseIcon'
import {MenuIcon} from '@/components/ui/icons/MenuIcon'
import {Label, Text} from '@/components/ui/Typography'
import {useTranslations} from '@/i18n/hooks/useTranslations'
import {Link} from '@/i18n/navigation'
import {type Navigation, type NavigationLink} from '@/types/navigation'
import {useState} from 'react'
import {LocaleSwitcher} from '../LocaleSwitcher'

interface Props {
  navigation: Navigation
}

const MobileNavLink = ({link, onNavigate}: {link: NavigationLink; onNavigate: () => void}) => {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" onClick={onNavigate} className="block">
        <Text className="text-gray-300 hover:text-white">{link.name}</Text>
      </a>
    )
  }

  return (
    <Link href={link.href} onClick={onNavigate} className="block">
      <Text className="text-gray-300 hover:text-white">{link.name}</Text>
    </Link>
  )
}

export const MobileMenu = ({navigation}: Props) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const closeMenu = () => setIsOpen(false)

  return (
    <div className="lg:hidden">
      {!isOpen && (
        <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)} className="relative z-50 ">
          <span className="sr-only">Open menu</span>
          <MenuIcon className="h-6 w-6" />
        </Button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-40 bg-[#001219] text-white">
          <div className="absolute top-4 right-4 z-50">
            <Button variant="ghost" size="icon" onClick={closeMenu} className="bg-black text-gray-300 hover:bg-gray-700">
              <span className="sr-only">Close menu</span>
              <CloseIcon className="h-12 w-12" />
            </Button>
          </div>

          <div className="pt-20 px-4 space-y-6">
            <div>
              <Label className="text-white/60 mb-2 block">{t('navigation.users')}</Label>
              <div className="ml-1 space-y-3 border-l border-white/15 pl-4">
                {navigation.users.links.map((link) => (
                  <MobileNavLink key={link.href} link={link} onNavigate={closeMenu} />
                ))}
              </div>
            </div>

            <div>
              <Label className="text-white/60 mb-2 block">{t('navigation.citiesAndPartners')}</Label>
              <div className="ml-1 space-y-3 border-l border-white/15 pl-4">
                {navigation.citiesAndPartners.links.map((link) => (
                  <MobileNavLink key={link.href} link={link} onNavigate={closeMenu} />
                ))}
              </div>
            </div>

            <div>
              <Link href="/support" onClick={closeMenu} className="block">
                <Text className="text-gray-300 hover:text-white">{t('navigation.support')}</Text>
              </Link>
            </div>

            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <LocaleSwitcher darkMode={true} />
              </div>
            </div>

            <Button asChild className="w-full text-base px-6 py-6 font-semibold">
              <Link href="/download" onClick={closeMenu}>
                {t('navigation.downloadApp')}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
